import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import AuthCodeModal from '../../components/MFA/Totp/AuthCodeModal';
import QrCodeModal from '../../components/MFA/Totp/QrCodeModal';
import EnableTwoFAModal from '../../components/MFA/Totp/EnableTwoFAModal';
import MFAEnabledModal from '../../components/MFA/Totp/MFAEnabledModal';
import PromptModal from '../../components/MFA/Totp/PromptModal';
import NotificationSent from '../../components/MFA/biometric/NotificationSent';
import NeedHelpModal from '../../components/MFA/biometric/NeedHelpModal';
import LogoutModal from '../../components/MFA/LogoutModal';
import Button from '../../widgets/Button';
import Input from '../../widgets/Input';
import { useMfaModalContext } from '../../../shared/providers/MfaModalProvider';

import useTracking from '../../../utilities/hooks/useTracking';
import useSignInWithEmail from './hooks/useSignInWithEmail';

import { EventActions, EventNames } from '../../../shared/constants/analytics';

import { ssoNotEnabledCode } from '../../../shared/constants/queries';
import { MfaModalState } from '../../../shared/constants/MFA';

import { useLogin } from '../../../utilities/helper/useLogin';
import { useMfa } from '../../../utilities/hooks/useMfa';
import LoginLayout from '../../components/LoginLayout';
import { MAX_RETRY_CODE } from '../../../shared/constants/ResetPassword';
import { StorageItems } from '../../../shared/constants/app';
import { removeItemFromLocalstorage } from '../../../utilities/common/storage';
import { setTokenData } from '../../../utilities/common/token';
import {
  LoginSuccessData,
  self,
  signInWithSSO,
} from '../../../shared/service/auth';

export default function Login() {
  const [useSSO, setUseSSO] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [ssoLoading, setSsoLoading] = useState(false);
  const [supportMessage, setSupportMessage] = useState<boolean>(false);
  const [logindata, setLogindata] = useState(null);
  const completedRef = useRef(false);

  const { t } = useTranslation();

  const { doLogin } = useLogin();
  const { refetchMfaCheck, authMeData } = useMfa();

  const { trackEvent } = useTracking();
  const {
    activeModal,
    logoutModal,
    needHelpModal,
    promptAlertModal,
    maxApiLimit,
    setMaxApiLimit,
    verifyCodeArgs,
    isMfaLoading,
    allowAccess,
    setAllowAccess,
  } = useMfaModalContext();

  const processTokens = (tokens?: LoginSuccessData) => {
    if (!tokens?.accessToken) return;
    setSsoLoading(false);
    completedRef.current = false;
    setAllowAccess({ login: false, layout: false });
    setTokenData({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresAt: tokens.expiresAt,
    });
    removeItemFromLocalstorage(StorageItems.ACCESS_TOKEN);
    refetchMfaCheck();
  };

  const { mutate: performNormalLogin, isPending: normalLoginLoading } =
    useSignInWithEmail(email, password, {
      onSuccess: (tokens: LoginSuccessData) => processTokens(tokens),
      onError: (err: any) => {
        if (err?.status === MAX_RETRY_CODE) {
          setError(t('AUTH_TOO_MANY_RETRY'));
        } else {
          setError(t('ssoTokenVerifyFailedHeading') ?? 'Authentication failed.');
        }
      },
    });

  const { mutate: performSSOLogin, isPending: ssoLoginLoading } = useMutation({
    mutationFn: () => signInWithSSO(email),
    onSuccess: (data) => {
      if (!data?.loginUrl) {
        setError(t('ssoTokenVerifyFailedHeading') ?? 'Authentication failed.');
        return;
      }
      if (useSSO) {
        setSsoLoading(true);
        window.location.href = data.loginUrl;
      } else {
        setError(
          t('ssoForEmailIsActive') ??
            'This email is associated with SSO. Please login with SSO.',
        );
      }
    },
    onError: (errorData: any) => {
      if (useSSO) {
        const ssoCode =
          errorData?.data?.code ?? errorData?.data?.override?.code;
        if (String(ssoCode) === String(ssoNotEnabledCode)) {
          setError(
            t('orgNotActivatedSSO') ?? 'This domain is not yet SSO-enabled.',
          );
        } else {
          setError(
            t('ssoTokenVerifyFailedHeading') ?? 'Authentication failed.',
          );
        }
      } else {
        performNormalLogin();
      }
    },
  });

  const handleSubmit = (e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setMaxApiLimit(false);
    performSSOLogin();
  };

  const handleUseSsoChange = () => {
    setUseSSO(!useSSO);
    setError('');
  };

  useEffect(() => {
    const finishLogin = async () => {
      if (!allowAccess.login || !allowAccess.layout || completedRef.current) {
        return;
      }
      completedRef.current = true;
      try {
        const meResponse: any = await self();
        setLogindata(meResponse?.data);
        const result = await doLogin(meResponse?.data);
        if (!result.success) {
          completedRef.current = false;
          setError(result.error?.message || '');
        }
      } catch (e) {
        completedRef.current = false;
        setError(t('ssoTokenVerifyFailedHeading') ?? 'Authentication failed.');
      }
    };

    finishLogin();
  }, [allowAccess.login, allowAccess.layout]);

  useEffect(() => {
    setTimeout(() => {
      if (supportMessage) {
        setSupportMessage(false);
      }
    }, 3000);
  }, [supportMessage]);

  useEffect(() => {
    if ((authMeData as any)?.data) {
      setLogindata((authMeData as any).data);
    }
  }, [authMeData]);

  useEffect(() => {
    trackEvent(EventNames.LoginScreen, {
      eventAction: EventActions.click,
      eventCategory: EventNames.LoginScreen,
      eventLabel: 'landing',
    });
  }, []);

  function handleEvents(EventLabel: string, EventSource: string) {
    trackEvent(EventNames.WellBeingDashboard, {
      eventLabel: EventLabel,
      eventAction: EventActions.click,
      eventCategory: EventLabel,
      eventSource: EventSource,
    });
  }

  return (
    <LoginLayout title="Sign in" subTitle="Welcome back, we’ve missed you!">
      <form
        id="sign-in"
        className="mt-4 grid gap-3 py-5 xl:mt-6 xl:py-0 2xl:gap-5"
        onSubmit={handleSubmit}
      >
        <Input
          label="Email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        {!useSSO && (
          <div>
            <Input
              label="Password"
              type="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <div className="mt-[6px] flex flex-col">
              <Link
                to="/forgot-password"
                className="text-primary-600 text-sm"
                onClick={() => handleEvents('forgot_password', 'login_screen')}
              >
                Forgot password ?
              </Link>
            </div>
          </div>
        )}
        {error && <p className="text-xs text-red-500">{error}</p>}
        {maxApiLimit && (
          <p className="text-xs text-red-500">{t('MANY_RETRY')}</p>
        )}

        <div className="mt-12 grid gap-y-4">
          <Button
            type="submit"
            size="lg"
            form="sign-in"
            disabled={email === '' || (useSSO ? false : password === '')}
            isLoading={
              normalLoginLoading ||
              isMfaLoading ||
              ssoLoginLoading ||
              ssoLoading
            }
            className="font-semibold"
            onClick={() => {
              handleEvents(
                useSSO ? 'sso_login_continue' : 'login_after_password',
                useSSO ? 'sso_screen' : 'login_screen',
              );
            }}
          >
            Continue
          </Button>

          <Button
            size="lg"
            variant="outline"
            className="!text-sm"
            onClick={() => {
              handleUseSsoChange();
              handleEvents(
                useSSO ? 'use_password ' : 'sso_login',
                useSSO ? 'sso_screen' : 'login_screen',
              );
            }}
          >
            {t(useSSO ? 'passwordLoginButtonText' : ' Use SSO instead')}
          </Button>
        </div>
      </form>

      {activeModal === MfaModalState.NotificationSent && (
        <NotificationSent
          enabled={activeModal === MfaModalState.NotificationSent}
        />
      )}

      <NeedHelpModal enabled={needHelpModal} userData={logindata} />

      <EnableTwoFAModal enabled={activeModal === MfaModalState.Setup2Fa} />

      {activeModal === MfaModalState.ScanQrCode && (
        <QrCodeModal
          enabled={activeModal === MfaModalState.ScanQrCode}
          data={verifyCodeArgs}
        />
      )}

      {activeModal === MfaModalState.Enter2FaCode && (
        <AuthCodeModal
          data={verifyCodeArgs}
          enabled={activeModal === MfaModalState.Enter2FaCode}
        />
      )}
      {logoutModal && <LogoutModal open={logoutModal} />}
      <MFAEnabledModal enabled={activeModal === MfaModalState.Enabled2Fa} />

      <PromptModal
        enabled={promptAlertModal}
        userData={logindata ?? { name: email }}
      />
    </LoginLayout>
  );
}
